<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import NextButton from 'dashboard/components-next/button/Button.vue';
import MessageList from './MessageList.vue';
import AilabAssistant from 'dashboard/api/ailab/assistant';

const { assistantId, isAIGamer } = defineProps({
  assistantId: {
    type: Number,
    required: true,
  },
  isAIGamer: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const messages = ref([]);
const newMessage = ref('');
const isLoading = ref(false);

const imageUrl = ref('');
const imageFile = ref(null);
const imagePreviewUrl = ref('');
const fileInputRef = ref(null);
const hasImageInput = computed(
  () => isAIGamer && (imageUrl.value.trim() || imageFile.value)
);

const formatMessagesForApi = () => {
  return messages.value.map(message => ({
    role: message.sender,
    content: message.apiContent ?? message.content,
  }));
};

const resetConversation = () => {
  messages.value = [];
  newMessage.value = '';
  imageUrl.value = '';
  imageFile.value = null;
  if (imagePreviewUrl.value?.startsWith?.('blob:'))
    URL.revokeObjectURL(imagePreviewUrl.value);
  imagePreviewUrl.value = '';
};

const openFilePicker = () => {
  fileInputRef.value?.click();
};

const onFileChange = e => {
  const file = e?.target?.files?.[0];
  if (!file) return;
  imageFile.value = file;
  if (imagePreviewUrl.value?.startsWith?.('blob:'))
    URL.revokeObjectURL(imagePreviewUrl.value);
  imagePreviewUrl.value = URL.createObjectURL(file);
};

const clearImage = () => {
  imageUrl.value = '';
  imageFile.value = null;
  if (imagePreviewUrl.value?.startsWith?.('blob:'))
    URL.revokeObjectURL(imagePreviewUrl.value);
  imagePreviewUrl.value = '';
  if (fileInputRef.value) fileInputRef.value.value = '';
};

const normalizeAssistantText = text => {
  const s = (text || '').toString().trim();
  if (s === 'conversation_handoff') {
    // This is a backend sentinel used to trigger handoff in real conversations.
    // In playground, show a friendly message instead of leaking the sentinel.
    return t('AILAB.PLAYGROUND.HANDOFF_MESSAGE');
  }
  return s;
};

const extractReasoning = data => {
  if (!data) return '';
  const r = data?.reasoning || data?.error || '';
  return (r || '').toString().trim();
};

const extractAssistantContent = data => {
  if (!data) return '';
  const direct = (data?.response || '').toString();
  if (direct.trim()) return direct;

  // AI Closer may return multi-segment `responses` without a flattened `response`.
  const arr = Array.isArray(data?.responses) ? data.responses : [];
  const parts = arr
    .map(item => {
      if (item && typeof item === 'object')
        return item.content || item.response || '';
      return item || '';
    })
    .map(s => (s || '').toString().trim())
    .filter(Boolean);
  return parts.join('\n\n');
};

const sendMessage = async () => {
  if ((!newMessage.value.trim() && !hasImageInput.value) || isLoading.value)
    return;

  const userMessage = {
    content: newMessage.value,
    apiContent: null,
    imagePreviewUrl: '',
    sender: 'user',
    timestamp: new Date().toISOString(),
  };

  if (hasImageInput.value) {
    const parts = [];
    if (newMessage.value.trim())
      parts.push({ type: 'text', text: newMessage.value });
    const url = imageUrl.value.trim();
    if (url) parts.push({ type: 'image_url', image_url: { url } });
    // If user attached a file, the backend will convert to a URL; we just signal the intent here.
    userMessage.apiContent = parts.length ? parts : newMessage.value;
    userMessage.imagePreviewUrl = imageFile.value ? imagePreviewUrl.value : url;
  }

  messages.value.push(userMessage);
  const currentMessage = newMessage.value;
  newMessage.value = '';

  try {
    isLoading.value = true;
    const { data } = await AilabAssistant.playground({
      assistantId,
      messageContent: currentMessage,
      messageHistory: formatMessagesForApi(),
      imageUrl: imageUrl.value.trim() || null,
      imageFile: imageFile.value,
    });

    const assistantText = normalizeAssistantText(extractAssistantContent(data));
    messages.value.push({
      content:
        assistantText ||
        'Sorry — something went wrong on our side. Please try again in a moment.',
      debugReasoning: extractReasoning(data),
      sender: 'assistant',
      timestamp: new Date().toISOString(),
    });
    clearImage();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error getting assistant response:', error);
    messages.value.push({
      content:
        'Sorry — something went wrong on our side. Please try again in a moment.',
      debugReasoning:
        error?.response?.data?.error ||
        error?.message ||
        'Playground request failed',
      sender: 'assistant',
      timestamp: new Date().toISOString(),
    });
  } finally {
    isLoading.value = false;
  }
};

const onInputKeydown = e => {
  if (e?.key !== 'Enter') return;
  // Enter sends, Shift+Enter inserts newline.
  if (e.shiftKey) return;
  e.preventDefault();
  sendMessage();
};
</script>

<template>
  <div
    class="flex flex-col h-full min-h-0 rounded-lg p-4 border border-n-slate-4 text-n-slate-11"
  >
    <MessageList
      :messages="messages"
      :is-loading="isLoading"
      :compact-image-bubble="isAIGamer"
      :image-class="
        isAIGamer
          ? 'mt-2 max-h-56 w-auto rounded-md border border-transparent ring-1 ring-black/10'
          : undefined
      "
    />

    <div v-if="isAIGamer" class="mb-3">
      <div class="flex items-center gap-2">
        <input
          v-model="imageUrl"
          class="flex-1 bg-n-solid-1 outline outline-n-container rounded-lg p-2 text-sm mb-0"
          placeholder="Image URL (optional) - paste a direct image link"
        />
        <input
          ref="fileInputRef"
          type="file"
          accept="image/*"
          class="hidden"
          @change="onFileChange"
        />
        <NextButton
          ghost
          size="small"
          icon="i-lucide-image-plus"
          @click="openFilePicker"
        />
        <NextButton
          v-if="hasImageInput"
          ghost
          size="small"
          icon="i-lucide-x"
          @click="clearImage"
        />
      </div>
      <p v-if="imageFile" class="text-xs text-n-slate-11 mt-1">
        Selected: {{ imageFile.name }}
      </p>
    </div>

    <div
      class="flex items-center bg-n-solid-1 outline outline-n-container rounded-lg p-3"
    >
      <textarea
        v-model="newMessage"
        class="flex-1 bg-transparent border-none focus:outline-none text-sm mb-0 resize-none leading-5"
        :placeholder="t('AILAB.PLAYGROUND.MESSAGE_PLACEHOLDER')"
        rows="3"
        style="min-height: 88px; max-height: 220px; overflow: auto"
        @keydown="onInputKeydown"
      />
      <NextButton
        ghost
        size="small"
        :disabled="!newMessage.trim() && !hasImageInput"
        icon="i-lucide-send"
        @click="sendMessage"
      />
    </div>

    <p class="text-xs text-n-slate-11 pt-2 text-center">
      {{ t('AILAB.PLAYGROUND.CREDIT_NOTE') }}
    </p>
  </div>
</template>
